import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { PlusIcon, PencilIcon, TrashIcon, LoaderIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { mcpClassApi } from '@/api/mcpClass.api';
import { formatTimestamp } from '@/utils/utils';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { McpIcon, EmptyMcpIcon, ErrorCircleIcon } from './shared';
import { MCP_TRANSPORT_LABELS } from './mcp-constants';

export default function MCPClasses() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['mcp-classes', page, pageSize, searchKeyword],
    queryFn: () =>
      mcpClassApi.listMCPClasses(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: searchKeyword.trim() ? { name: searchKeyword.trim() } : {},
        })
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => mcpClassApi.deleteMCPClass(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['mcp-classes'] });
    },
  });

  const items = (listData?.data || []).filter(Boolean);
  const keyword = searchKeyword.trim();
  const totalPages = Math.max(1, Math.ceil((listData?.total || 0) / pageSize));

  return (
    <>
      <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <McpIcon />
            <span className="text-sm font-medium text-foreground">MCP 模版</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="搜索名称..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="h-9 w-48 bg-muted/50 border-0 focus:bg-background"
            />
            <Button
              size="sm"
              onClick={() => navigate('/plugins/mcps/classes/new')}
              className="gap-1.5"
            >
              <PlusIcon className="h-4 w-4" />
              新建
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="divide-y">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-24" />
                <div className="ml-auto flex gap-2">
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-destructive">
              <ErrorCircleIcon />
            </span>
            <p className="font-semibold text-foreground mb-1 mt-4">加载失败</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5 mt-3">
              重试
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <EmptyMcpIcon />
            <p className="font-semibold text-foreground mt-4 mb-1">
              {keyword ? '没有找到匹配的 MCP 模版' : '暂无 MCP 模版'}
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              {keyword ? '尝试其他关键词搜索' : '创建一个 MCP 模版作为模板'}
            </p>
            {!keyword && (
              <Button
                size="sm"
                onClick={() => navigate('/plugins/mcps/classes/new')}
                className="gap-1.5"
              >
                <PlusIcon className="h-4 w-4" />
                新建 MCP 模版
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-11">名称</TableHead>
                <TableHead className="h-11">Server URL</TableHead>
                <TableHead className="h-11 w-28">传输</TableHead>
                <TableHead className="h-11 w-20">参数</TableHead>
                <TableHead className="h-11 w-28">创建时间</TableHead>
                <TableHead className="h-11 w-28">更新时间</TableHead>
                <TableHead className="h-11 w-72">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="group">
                  <TableCell className="font-medium py-4">
                    <div className="min-w-0">
                      <Link
                        to={`/plugins/mcps/classes/${item.id}`}
                        className="text-primary hover:text-primary/80 font-medium text-left truncate block"
                      >
                        {item.name}
                      </Link>
                      <span className="text-xs text-muted-foreground/60">ID: {item.id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground truncate max-w-[260px]">
                    {item.template?.serverUrl || '-'}
                  </TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {MCP_TRANSPORT_LABELS[item.template?.transport ?? 0] || '-'}
                  </TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {item.params?.length || 0}
                  </TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {formatTimestamp(item.createdAt)}
                  </TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground">
                    {formatTimestamp(item.updatedAt)}
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => navigate(`/plugins/mcps/classes/${item.id}/instances/new`)}
                      >
                        实例化
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => navigate(`/plugins/mcps/classes/${item.id}/edit`)}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeleteId(item.id)}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && !error && items.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4">
            <p className="text-sm text-muted-foreground">
              共 <span className="font-medium text-foreground">{listData?.total || 0}</span> 条
            </p>
            <div className="flex items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                上一页
              </Button>
              <span className="text-xs">
                {page} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除该 MCP 模版吗？关联实例不会被自动删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
